import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, Loader2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        address: '',
        password: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const { register } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API_URL}/api/settings`).then(res => {
            if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
        }).catch(() => {});
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        const result = await register({
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            mobile: formData.mobile,
            address: formData.address,
            password: formData.password,
        });

        if (result.success) {
            navigate('/parent');
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    return (
        <div className="min-h-screen flex relative">
            {/* School logo background */}
            <div className="absolute inset-0 z-0">
                {logoUrl && (
                    <div className="absolute inset-0 bg-center bg-no-repeat" style={{ backgroundImage: `url(${logoUrl})`, backgroundSize: 'contain', opacity: 0.18 }} />
                )}
                <div className="absolute inset-0 bg-[#FAF9F6]/90" />
            </div>

            {/* Left side - Hero section */}
            <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col justify-center items-center p-12">
                {logoUrl && <img src={logoUrl} alt="School Logo" className="w-40 h-40 mb-6 rounded-xl object-contain" />}
                <h1 className="text-4xl font-bold text-center mb-4 font-['Outfit'] text-[#064E3B]">
                    Nurulhuda Quranic School
                </h1>
                <p className="text-lg text-center text-[#4B5563] max-w-md">
                    Join our community and give your children the gift of Quranic education
                </p>
            </div>

            {/* Right side - Registration Form */}
            <div className="flex-1 flex flex-col justify-center items-center p-8 relative z-10 overflow-y-auto">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex flex-col items-center mb-6">
                        {logoUrl && <img src={logoUrl} alt="School Logo" className="w-16 h-16 mb-3 rounded-xl object-contain" />}
                        <h1 className="text-xl font-bold text-[#064E3B] font-['Outfit']">
                            Nurulhuda Quranic School
                        </h1>
                    </div>

                    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8">
                        <Link 
                            to="/login" 
                            className="inline-flex items-center text-[#4B5563] hover:text-[#1F2937] mb-4"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back to Login
                        </Link>

                        <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                            Parent Registration
                        </h2>
                        <p className="text-[#4B5563] mb-6">
                            Create your account to register your children
                        </p>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">First Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                        <Input
                                            id="first_name"
                                            name="first_name"
                                            type="text"
                                            placeholder="First name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            className="pl-9 input-field"
                                            required
                                            data-testid="register-first-name"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Last Name</Label>
                                    <Input
                                        id="last_name"
                                        name="last_name"
                                        type="text"
                                        placeholder="Last name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className="input-field"
                                        required
                                        data-testid="register-last-name"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email Address</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="pl-9 input-field"
                                        required
                                        data-testid="register-email"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="mobile">Mobile Number</Label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                    <Input
                                        id="mobile"
                                        name="mobile"
                                        type="tel"
                                        placeholder="Enter mobile number"
                                        value={formData.mobile}
                                        onChange={handleChange}
                                        className="pl-9 input-field"
                                        required
                                        data-testid="register-mobile"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="address">Home Address</Label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-[#4B5563]" />
                                    <Input
                                        id="address"
                                        name="address"
                                        type="text"
                                        placeholder="Enter your address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className="pl-9 input-field"
                                        required
                                        data-testid="register-address"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Create a password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="pl-9 pr-10 input-field"
                                        required
                                        data-testid="register-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#1F2937]"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                    <Input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Confirm your password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="pl-9 pr-10 input-field"
                                        required
                                        data-testid="register-confirm-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#1F2937]"
                                    >
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-start space-x-2">
                                <p className="text-xs text-[#4B5563] leading-tight italic">By creating an account, you agree to Nurulhuda Quranic School's terms and privacy policy.</p>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#064E3B] hover:bg-[#047857] text-white h-11 mt-2"
                                data-testid="register-submit-button"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating Account...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-[#4B5563]">
                                Already have an account?{' '}
                                <Link 
                                    to="/login" 
                                    className="text-[#064E3B] hover:text-[#047857] font-medium"
                                >
                                    Sign In
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-6 text-center text-sm text-[#4B5563]">
                        <p>© {new Date().getFullYear()} Nurulhuda Quranic School. All rights reserved.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
